/**
 * Created by Ning Jiang on 2023-08-29.
 */

import {DevilsCrossRoad} from "../DevilsCrossRoad";
import {gsap, TimelineLite} from "gsap";
import {DCRoadReelsController} from "../reels/DCRoadReelsController";
import {DevilsCrossRoadSound} from "../sound/DevilsCrossRoadSound";

export class DCRoadSymbol extends PIXI.Container {

    public symbolName:string;
    public reelId:number;
    public symId:number;
    public isRemoved:boolean = false;
    public isWin:boolean = false;
    public isForbidden:boolean = false;
    public isMulti:boolean = false;
    public highlightPlayed:boolean;

    protected _animationContainer:PIXI.Container;
    protected _spine:PIXI.spine.Spine;
    private _shakeTl:TimelineLite;

    constructor(symbolName:string, reelId:number, symId:number) {
        super();
        this.symbolName = symbolName;
        this.reelId = reelId;
        this.symId = symId;
        this.initAnimation();
    }

    protected initAnimation():void {
        this._animationContainer = new PIXI.Container();
        this.addChild(this._animationContainer);
        this.initSpine();
    }

    protected initSpine():void {
        if(!this._spine) {
            this._spine = new PIXI.spine.Spine(DevilsCrossRoad.assetLoader.getSpineAsset(this.symbolName[0]));
            this._spine.skeleton.setSkinByName(this.symbolName);
            this._spine.autoUpdate = true;
            // this._animationContainer.addChild(this._spine);
        }
    }

    public getGlobalPosition():PIXI.Point {
        if(this.parent) {
            return new PIXI.Point(this.x + this.parent.x,
                this.y + this.parent.y);
        }
        return this.position;
    }

    public onRemove():void {
        this._spine.state.clearTracks();
        this._spine.skeleton.setToSetupPose();
        this._spine.state.setEmptyAnimations(0);
        this._animationContainer.removeChild(this._spine);
        this._spine.destroy();
        this.destroy({children : true});
        this.isRemoved = true;
    }

    public playLanding(init:boolean):TimelineLite {
        const tl = gsap.timeline();
        tl.add(() => {
            this._animationContainer.addChild(this._spine);
            let entry = this._spine.state.setAnimation(0, "landing", false);
            if(init) {
                entry.trackTime = entry.animation.duration;
            } else {
                if(this.symbolName !== "W" && this.symbolName !== "S") {
                    DevilsCrossRoadSound.playSymLandingSound(this.reelId, this.symId);
                }
            }
        });
        if(!init) {
            tl.add(() => {
                if(DevilsCrossRoad.scatters.isNearWin) {
                    this.shakeNearWin();
                }
            }, this._spine.spineData.findAnimation("landing").duration);
        }
        if(init || this.symbolName.includes("W") || this.symbolName.includes("S") ||
            (this.reelId > 0 && this.reelId < 4 && this.symId > 0 && this.symId < 4)) {
            return tl;
        }
        // play symbol preWin highlight
        const devWay = DCRoadReelsController.DEV_WAYS_MAP[this.reelId][this.symId];
        for(let i = 0; i < DevilsCrossRoad.model.gameData.betWayWins.length; i++) {
            const betWayWins = DevilsCrossRoad.model.gameData.betWayWins[i];
            if(betWayWins.devWay === devWay && betWayWins.symbol === this.symbolName) {
                const middleCoord = DCRoadReelsController.DEV_WAY_COORDS[devWay][0];
                const midSymbol = DevilsCrossRoad.reels.getSymbol(middleCoord[0], middleCoord[1]);
                if(betWayWins.symbol === midSymbol!.symbolName) { // exclude win by surprise W
                    const highlightTl = gsap.timeline();
                    let playSound = false;
                    DCRoadReelsController.DEV_WAY_COORDS[devWay].forEach(coord => {
                        const sym = DevilsCrossRoad.reels.getSymbol(coord[0], coord[1]);
                        if(sym && sym.symbolName === betWayWins.symbol && !sym.highlightPlayed) {
                            highlightTl.add(sym.playPreWin(), 0);
                            playSound = true;
                        }
                    });
                    if(playSound) {
                        highlightTl.add(() => {DevilsCrossRoadSound.playWinHighlight();}, 0);
                    }
                    tl.add(highlightTl, 0);
                }
            }
        }

        return tl;
    }

    private playSpineAnimation(animationName:string, trackIndex:number = 0):TimelineLite {
        const tl = gsap.timeline();
        tl.add(() => {
            this._spine.state.setAnimation(trackIndex, animationName, false);
        });
        tl.add(() => {
        }, `+=${this._spine.spineData.findAnimation(animationName).duration}`);
        return tl;
    }

    public playSpinRemove():TimelineLite {
        return this.playSpineAnimation("spin_remove");
    }

    public playPreWin():TimelineLite {
        this.highlightPlayed = true;
        const tl = this.playSpineAnimation("pre_win", 1);
        tl.add(() => {
            this._spine.state.setEmptyAnimation(1, 0.1);
        });
        return tl;
    }

    public playWinAnimation(winLevel:number):TimelineLite {
        return this.playSpineAnimation("win");
    }

    public playDisintegrateAnimation():TimelineLite {
        const tl = this.playSpineAnimation("blank_transition");
        tl.add(() => {
            this._spine.skeleton.setSkinByName("Blank");
        });
        return tl;
    }

    public playWinRemove():TimelineLite {
        return this.playSpineAnimation("win_remove");
    }

    public playForbidden():TimelineLite {
        if(this.symbolName === "S") {
            return gsap.timeline();
        }

        this.isForbidden = true;
        return this.playSpineAnimation("forbidden");
    }

    public playAvalanche?():TimelineLite;

    public shakeNearWin():void {
        this.stopShakeTl();
        this.playShakeLoop();
    }

    public stopNearWin():void {
        this.stopShakeTl();
    }

    private playShakeLoop():void {
        const tl = gsap.timeline();
        tl.add(this.getShakeStageOnceTl(0.2));
        tl.add(() => {
                if(DevilsCrossRoad.scatters.isNearWin) {
                    this.playShakeLoop();
                }
            }
        );
    }

    public getShakeStageOnceTl(factor?:number):TimelineLite {
        if(!factor) {
            factor = 1;
        }
        const tl = gsap.timeline();
        tl.to(this._animationContainer, {
            x : (Math.random() * 32 - 16) * factor,
            y : (Math.random() * 16 - 8) * factor,
            rotation : (Math.random() * 2 - 1) * Math.PI / 90,
            duration : 0.02
        })
            .to(this._animationContainer, {x : 0, y : 0, rotation : 0, duration : 0.03});
        return tl;
    }

    private stopShakeTl():void {
        if(this._shakeTl && this._shakeTl.isActive()) {
            this._shakeTl.progress(1);
            this._shakeTl.kill();
        }
    }
}